// Copyright (c) 2026 Solartek LLC. All rights reserved.
/**
 * Attendance watchlist and observation (workforce controls §6, migration 20261025120400; customer requests #2 and #6).
 *
 *  - The WATCHLIST scores every employee in the caller's scope over a period (a week, a calendar month, a calendar year or a
 *    custom range of at most a year) from the daily records and the leave requests: late days and minutes, very late days,
 *    early departures, absences, unexcused days, missing-punch days, unplanned (after the fact) and emergency leave. The score
 *    is Σ weight × count (`organization_settings.watchlist.weights`); an employee is FLAGGED when the score or any non-zero
 *    threshold is reached — the thresholds are per month and scale to the period (`watchlistThresholdScale`). Each row says
 *    why (`reasons`) and carries the previous period's score (the trend).
 *  - An OBSERVATION places one employee under watch with a reason and a review date, and is closed with an outcome. At most one
 *    is active per employee. HR (observation.manage) places anybody in their branch scope; a line manager
 *    (observation.manage_team) places their direct reports when the organisation lets managers place (`managersCanPlace`);
 *    the monthly run places every flagged employee when `autoObservation` is on (source AUTO).
 * The pure rules (period, scaling, scoring, reasons, sorting) live in @flowza/domain `watchlist`; the figures in
 * @flowza/database `loadWatchlistFacts` — one definition for the page, the employee detail, the monthly run and the report.
 */
import { z } from 'zod';
import { booleanQuerySchema, isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
// ----- periods ---------------------------------------------------------------------------------------------------------------
/** week = the week containing `date` (starting on the organisation's first day of the week); month / year = calendar ones. */
export const WATCHLIST_PERIODS = ['week', 'month', 'year', 'custom'];
/** The longest custom period (days, both ends included) — a year of an organisation. */
export const WATCHLIST_MAX_DAYS = 366;
/**
 * The factor the per-month thresholds are multiplied by for a period: week ¼, month 1, year 12, custom days ÷ 30. A non-zero
 * threshold never scales to "not used": counts and minutes round half up to at least 1, the score to the nearest half point,
 * at least 0.5 (`scaleWatchlistThresholds` in @flowza/domain).
 */
export function watchlistThresholdScale(kind, days) {
    if (kind === 'week')
        return 0.25;
    if (kind === 'month')
        return 1;
    if (kind === 'year')
        return 12;
    return Math.max(1, days) / 30;
}
// ----- figures -----------------------------------------------------------------------------------------------------------------
/** What is counted per employee and period. A very late day is a late day too (the very-late weight is an extra penalty). */
export const WATCHLIST_METRICS = ['lateDays', 'lateMinutes', 'veryLateDays', 'earlyDays', 'earlyMinutes', 'absentDays', 'unexcusedDays', 'missingPunchDays', 'unplannedLeave', 'emergencyLeave'];
/** The weighed incident kinds — the keys of `watchlist.weights` — and the metric each one counts. */
export const WATCHLIST_INCIDENTS = ['late', 'veryLate', 'earlyDeparture', 'absent', 'unexcused', 'missingPunch', 'unplannedLeave', 'emergencyLeave'];
export const WATCHLIST_INCIDENT_METRIC = {
    late: 'lateDays', veryLate: 'veryLateDays', earlyDeparture: 'earlyDays', absent: 'absentDays', unexcused: 'unexcusedDays',
    missingPunch: 'missingPunchDays', unplannedLeave: 'unplannedLeave', emergencyLeave: 'emergencyLeave',
};
/** Why a row is flagged: the score or a threshold reached (`value` ≥ `threshold`, the threshold scaled to the period). */
export const WATCHLIST_REASON_CODES = ['SCORE', 'LATE_DAYS', 'LATE_MINUTES', 'ABSENT_DAYS', 'UNEXCUSED_DAYS'];
// ----- the list -------------------------------------------------------------------------------------------------------------
/** `team`: the caller's direct reports only; `all`: everybody in the caller's scope (HR — a line manager always sees `team`). */
export const WATCHLIST_SCOPES = ['team', 'all'];
export const WATCHLIST_OBSERVATION_FILTERS = ['any', 'active', 'none'];
/** Sort keys; the figures sort high first and the name A→Z unless `order` says otherwise; `trend` = score − previous score. */
export const WATCHLIST_SORTS = ['score', 'lateDays', 'lateMinutes', 'absentDays', 'unexcusedDays', 'earlyDays', 'trend', 'name'];
/** The UTC midnight of a real calendar date (YYYY-MM-DD), or null for an impossible one (2026-02-31, 2000-13-01). */
const utcDay = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d)
        return null;
    const t = Date.UTC(y, m - 1, d);
    const back = new Date(t);
    return back.getUTCFullYear() === y && back.getUTCMonth() === m - 1 && back.getUTCDate() === d ? t : null;
};
/**
 * A real calendar date: the shape alone would let 2026-02-31 through, which the period resolver then silently replaces — and
 * the 366-day bound would be checked on dates that are never scored.
 */
const calendarDateSchema = isoDateSchema.refine((s) => utcDay(s) !== null, 'Not a calendar date');
const periodFields = {
    period: z.enum(WATCHLIST_PERIODS).default('month'),
    /** Any day of the week / month / year to show (default: the organisation's today). */
    date: calendarDateSchema.optional(),
    /** The custom period (both required with `period=custom`). */
    from: calendarDateSchema.optional(),
    to: calendarDateSchema.optional(),
};
const periodRefinement = (v, ctx) => {
    if (v.period !== 'custom')
        return;
    if (!v.from || !v.to) {
        ctx.addIssue({ code: 'custom', path: [v.from ? 'to' : 'from'], message: 'A custom period needs from and to' });
        return;
    }
    const a = utcDay(v.from);
    const b = utcDay(v.to);
    if (a === null || b === null)
        return; // reported by the field itself
    if (b < a) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The end cannot be before the start' });
        return;
    }
    if ((b - a) / 86_400_000 + 1 > WATCHLIST_MAX_DAYS)
        ctx.addIssue({ code: 'custom', path: ['to'], message: `A custom period covers at most ${WATCHLIST_MAX_DAYS} days` });
};
/** GET /orgs/:orgId/watchlist */
export const watchlistQuerySchema = paginationQuerySchema.extend({
    ...periodFields,
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    /** HR only (attendance.view): the employees whose primary or secondary manager is this employee. */
    managerEmployeeId: uuidSchema.optional(),
    scope: z.enum(WATCHLIST_SCOPES).optional(),
    flagged: booleanQuerySchema.optional(),
    observation: z.enum(WATCHLIST_OBSERVATION_FILTERS).default('any'),
    search: z.string().trim().max(100).optional(),
    sort: z.enum(WATCHLIST_SORTS).default('score'),
    /** Absent: the figures high → low, the name A → Z. */
    order: z.enum(['asc', 'desc']).optional(),
}).superRefine(periodRefinement);
/** GET /orgs/:orgId/watchlist/employees/:employeeId — the same period parameters. */
export const watchlistDetailQuerySchema = z.object(periodFields).superRefine(periodRefinement);
// ----- the employee detail ------------------------------------------------------------------------------------------------------
/** The day-level incident kinds (leave requests are listed apart). */
export const WATCHLIST_DAY_INCIDENTS = ['late', 'veryLate', 'earlyDeparture', 'absent', 'unexcused', 'missingPunch'];
// ----- observations --------------------------------------------------------------------------------------------------------------
export const OBSERVATION_STATUSES = ['ACTIVE', 'CLOSED'];
export const OBSERVATION_SOURCES = ['MANUAL', 'AUTO'];
export const OBSERVATION_OUTCOMES = ['IMPROVED', 'NOT_IMPROVED', 'ESCALATED', 'CANCELLED'];
/** The conflict reason when the employee already has an active observation (409). */
export const OBSERVATION_ACTIVE_CONFLICT = 'OBSERVATION_ACTIVE';
const reasonSchema = z.string().trim().min(3, 'Say why in a few words').max(1000);
const closingNoteSchema = z.string().trim().max(1000);
/**
 * POST /orgs/:orgId/observations. `startsOn` defaults to the organisation's today and `reviewOn` to the start +
 * `defaultObservationDays`; `periodFrom` / `periodTo` name the watchlist period that justified it (its row is stored as the
 * snapshot) — absent: the current month.
 */
export const observationCreateSchema = z.object({
    employeeId: uuidSchema,
    reason: reasonSchema,
    startsOn: calendarDateSchema.optional(),
    reviewOn: calendarDateSchema.optional(),
    periodFrom: calendarDateSchema.optional(),
    periodTo: calendarDateSchema.optional(),
}).superRefine((v, ctx) => {
    if (v.startsOn && v.reviewOn && v.reviewOn < v.startsOn)
        ctx.addIssue({ code: 'custom', path: ['reviewOn'], message: 'The review cannot be before the start' });
    if (!!v.periodFrom !== !!v.periodTo)
        ctx.addIssue({ code: 'custom', path: [v.periodFrom ? 'periodTo' : 'periodFrom'], message: 'Give both ends of the period' });
    if (v.periodFrom && v.periodTo && v.periodTo < v.periodFrom)
        ctx.addIssue({ code: 'custom', path: ['periodTo'], message: 'The end cannot be before the start' });
});
/**
 * PATCH /orgs/:orgId/observations/:id — an active observation's reason and review date (extend / shorten). Explicit optional
 * fields, NO defaults (AGENTS.md Zod 4 pitfall: a one-field PATCH changes one field).
 */
export const observationUpdateSchema = z.object({
    reason: reasonSchema.optional(),
    reviewOn: calendarDateSchema.optional(),
}).refine((v) => v.reason !== undefined || v.reviewOn !== undefined, { message: 'Nothing to change' });
/** POST /orgs/:orgId/observations/:id/close */
export const observationCloseSchema = z.object({
    outcome: z.enum(OBSERVATION_OUTCOMES),
    note: closingNoteSchema.optional(),
});
/** GET /orgs/:orgId/observations — newest first (active ones by review date with `sort=reviewOn`). */
export const OBSERVATION_SORTS = ['placedAt', 'reviewOn', 'name'];
export const observationListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(OBSERVATION_STATUSES).optional(),
    source: z.enum(OBSERVATION_SOURCES).optional(),
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    managerEmployeeId: uuidSchema.optional(),
    scope: z.enum(WATCHLIST_SCOPES).optional(),
    /** Active observations whose review date has passed. */
    overdue: booleanQuerySchema.optional(),
    search: z.string().trim().max(100).optional(),
    sort: z.enum(OBSERVATION_SORTS).default('placedAt'),
    order: z.enum(['asc', 'desc']).optional(),
});
//# sourceMappingURL=watchlist.js.map